import React, { useMemo } from 'react';
import {
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { Text } from '@/components/common/text';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import { RADIUS } from '@/constants/shared/theme';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import type { Cadence, Occurrence, TodoList } from '@/types/admin/todoLists';
import { ToDosScreenSkeleton } from '../ToDosScreenSkeleton';

// ---------------------------------------------------------------------------
// Pure helpers — shared with the desktop view so the two surfaces never drift.
// ---------------------------------------------------------------------------

/** Section ordering for the cadence groups (daily → weekly → monthly). */
export const CADENCE_ORDER: readonly Cadence[] = ['daily', 'weekly', 'monthly'];

export const CADENCE_LABEL: Record<Cadence, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

export interface CadenceGroup {
  cadence: Cadence;
  lists: TodoList[];
}

/**
 * Bucket lists into cadence groups in a stable order, dropping empty buckets.
 * The API already returns active lists for the `mine` scope, so no filtering here.
 */
export function groupByCadence(lists: TodoList[]): CadenceGroup[] {
  return CADENCE_ORDER.map(cadence => ({
    cadence,
    lists: lists.filter(list => list.cadence === cadence),
  })).filter(group => group.lists.length > 0);
}

export interface OccurrenceProgress {
  done: number;
  total: number;
  completed: boolean;
}

/**
 * Occurrence progress = (done + skipped) / total over the current occurrence's
 * items, EXCLUDING any item with `hiddenAt` set (those are retained for history
 * but excluded from occurrence-status recomputation — mirrors the backend).
 * Returns null when the list has no current occurrence (out of window).
 */
export function computeProgress(
  occurrence: Occurrence | null,
): OccurrenceProgress | null {
  if (!occurrence) return null;
  const visible = occurrence.items.filter(i => i.hiddenAt == null);
  const done = visible.filter(
    i => i.status === 'done' || i.status === 'skipped',
  ).length;
  return {
    done,
    total: visible.length,
    completed: occurrence.status === 'completed',
  };
}

/** The kind of target a list points at, for the role-vs-personal chip. */
export function targetKind(list: TodoList): 'role' | 'personal' | 'unassigned' {
  if (list.roleId != null) return 'role';
  if (list.assigneeAccountId != null) return 'personal';
  return 'unassigned';
}

// ---------------------------------------------------------------------------
// Presentational pieces (exported so the desktop view can reuse the row).
// ---------------------------------------------------------------------------

export const EMPTY_LABEL = 'No to-do lists yet';

/**
 * Which lists the index shows: `mine` (assigned to you or a role you hold) or
 * `all` (every list for the property, active + archived — staff-gated server-side).
 */
export type TodoScope = 'mine' | 'all';

const SCOPE_OPTIONS: ReadonlyArray<{
  scope: TodoScope;
  label: string;
  testID: string;
}> = [
  { scope: 'mine', label: 'My lists', testID: 'todos-scope-mine' },
  { scope: 'all', label: 'All lists', testID: 'todos-scope-all' },
];

/**
 * Two-option segmented toggle: [My lists] [All lists]. Follows the ItemStatusSelect
 * pattern — a plain View of "button" role pills, each reflecting its selected state
 * via accessibilityState. The selected pill fills with accent.purpleLight; the
 * others are transparent with a card.border outline (token colors only).
 */
export const ToDosScopeToggle: React.FC<{
  scope: TodoScope;
  onChange: (scope: TodoScope) => void;
}> = ({ scope, onChange }) => {
  const { redesign } = useAdminColors();
  return (
    <View testID="todos-scope-toggle" style={styles.scopeGroup}>
      {SCOPE_OPTIONS.map(({ scope: optionScope, label, testID }) => {
        const isSelected = optionScope === scope;
        const backgroundColor = isSelected
          ? redesign.accent.purpleLight
          : 'transparent';
        const borderColor = isSelected
          ? redesign.accent.purpleLight
          : redesign.card.border;
        const textColor = isSelected
          ? redesign.text.primary
          : redesign.text.muted;
        return (
          <TouchableOpacity
            key={optionScope}
            testID={testID}
            accessibilityRole="button"
            accessibilityState={{ selected: isSelected }}
            accessibilityLabel={label}
            onPress={() => onChange(optionScope)}
            style={[styles.scopeButton, { backgroundColor, borderColor }]}
          >
            <Text style={[styles.scopeButtonText, { color: textColor }]}>
              {label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

export const TargetChip: React.FC<{ list: TodoList }> = ({ list }) => {
  const { redesign } = useAdminColors();
  const kind = targetKind(list);
  const label =
    kind === 'role' ? 'Role' : kind === 'personal' ? 'Personal' : 'Unassigned';
  const bg =
    kind === 'role'
      ? redesign.accent.purpleLight
      : kind === 'personal'
      ? redesign.accent.blueBg
      : redesign.card.border;
  return (
    <View style={[styles.chip, { backgroundColor: bg }]}>
      <Text style={[styles.chipText, { color: redesign.text.primary }]}>
        {label}
      </Text>
    </View>
  );
};

/**
 * "Archived" chip for archived lists in the All-lists view. Muted treatment:
 * card.border background with muted text (per RES-163 token spec).
 */
export const ArchivedChip: React.FC = () => {
  const { redesign } = useAdminColors();
  return (
    <View style={[styles.chip, { backgroundColor: redesign.card.border }]}>
      <Text style={[styles.chipText, { color: redesign.text.muted }]}>
        Archived
      </Text>
    </View>
  );
};

/**
 * Compact bordered "Edit" affordance on an active row that jumps straight to the
 * edit form (skipping the occurrence screen). Deliberately quieter than the
 * occurrence header's filled "Edit list" action — one bordered chip per row keeps
 * the index scannable. Nested inside the row's pressable GlassCard, its own press
 * wins the touch responder so the row-body navigation does not also fire.
 */
const RowEditButton: React.FC<{ onPress: () => void }> = ({ onPress }) => {
  const { redesign } = useAdminColors();
  return (
    <TouchableOpacity
      testID="todo-list-row-edit"
      accessibilityRole="button"
      accessibilityLabel="Edit list"
      onPress={onPress}
      style={[styles.rowEditButton, { borderColor: redesign.card.border }]}
    >
      <Text style={[styles.rowEditText, { color: redesign.text.secondary }]}>
        Edit
      </Text>
    </TouchableOpacity>
  );
};

export const TodoListRow: React.FC<{
  list: TodoList;
  onPress?: (listId: number) => void;
  onEdit?: (listId: number) => void;
}> = ({ list, onPress, onEdit }) => {
  const { redesign } = useAdminColors();
  const progress = computeProgress(list.currentOccurrence);
  const isArchived = list.status === 'archived';
  // Archived rows read muted; active rows keep the primary title treatment.
  const titleColor = isArchived ? redesign.text.muted : redesign.text.primary;

  return (
    <GlassCard
      variant="soft"
      style={styles.row}
      interactive={onPress != null}
      onPress={onPress ? () => onPress(list.id) : undefined}
    >
      <View style={styles.rowHeader}>
        <Text style={[styles.title, { color: titleColor }]} numberOfLines={1}>
          {list.title}
        </Text>
        <View style={styles.rowHeaderRight}>
          {isArchived ? <ArchivedChip /> : <TargetChip list={list} />}
          {onEdit && !isArchived ? (
            <RowEditButton onPress={() => onEdit(list.id)} />
          ) : null}
        </View>
      </View>

      <View style={styles.rowMeta}>
        <Text style={[styles.cadence, { color: redesign.text.muted }]}>
          {CADENCE_LABEL[list.cadence]}
        </Text>

        {progress ? (
          <View style={styles.progressWrap}>
            <Text
              style={[
                styles.progress,
                {
                  color: progress.completed
                    ? redesign.accent.green
                    : redesign.text.secondary,
                },
              ]}
            >
              {progress.done}/{progress.total}
            </Text>
            {progress.completed && (
              <View
                style={[
                  styles.completedPill,
                  { backgroundColor: redesign.accent.greenBg },
                ]}
              >
                <Text
                  style={[
                    styles.completedText,
                    { color: redesign.accent.green },
                  ]}
                >
                  Completed
                </Text>
              </View>
            )}
          </View>
        ) : (
          <Text style={[styles.noPeriod, { color: redesign.text.muted }]}>
            {'—'}
          </Text>
        )}
      </View>
    </GlassCard>
  );
};

interface Props {
  lists: TodoList[];
  isLoading: boolean;
  scope: TodoScope;
  onScopeChange: (scope: TodoScope) => void;
  onSelectList?: (listId: number) => void;
  onCreateList?: () => void;
  onEditList?: (listId: number) => void;
}

const CreateButton: React.FC<{ onPress: () => void }> = ({ onPress }) => {
  const { redesign } = useAdminColors();
  return (
    <TouchableOpacity
      testID="todo-lists-create-button"
      onPress={onPress}
      style={[styles.createButton, { borderColor: redesign.card.border }]}
    >
      <Text style={[styles.createButtonText, { color: redesign.text.primary }]}>
        + New to-do list
      </Text>
    </TouchableOpacity>
  );
};

/**
 * Mobile To Do's index: a My/All lists toggle above cadence-grouped sections of
 * todo-list rows, with a loading skeleton and an empty state. Rows navigate to the
 * occurrence detail screen when `onSelectList` is provided; active rows also carry
 * an `onEditList` affordance that jumps straight to the edit form. `onCreateList`
 * adds a create entry point above the list. The toggle renders unconditionally so
 * it stays reachable in the loading/empty branches (a scope=all 403 yields no data
 * and falls into the empty state, from which the user can switch back to My lists).
 */
export const ToDosList: React.FC<Props> = ({
  lists,
  isLoading,
  scope,
  onScopeChange,
  onSelectList,
  onCreateList,
  onEditList,
}) => {
  const { redesign } = useAdminColors();
  const groups = useMemo(() => groupByCadence(lists), [lists]);

  const renderBody = (): React.ReactNode => {
    if (isLoading && lists.length === 0) {
      return <ToDosScreenSkeleton />;
    }

    if (lists.length === 0) {
      return (
        <View style={styles.centered}>
          <Text style={[styles.emptyText, { color: redesign.text.muted }]}>
            {EMPTY_LABEL}
          </Text>
        </View>
      );
    }

    return groups.map(group => (
      <View key={group.cadence} style={styles.section}>
        <Text style={[styles.sectionHeader, { color: redesign.text.subtle }]}>
          {CADENCE_LABEL[group.cadence]}
        </Text>
        {group.lists.map(list => (
          <TodoListRow
            key={list.id}
            list={list}
            onPress={onSelectList}
            onEdit={onEditList}
          />
        ))}
      </View>
    ));
  };

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.toolbar}>
        <ToDosScopeToggle scope={scope} onChange={onScopeChange} />
        {onCreateList ? <CreateButton onPress={onCreateList} /> : null}
      </View>
      {renderBody()}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { padding: 16, paddingBottom: 32, gap: 20 },
  centered: {
    flex: 1,
    minHeight: 200,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  emptyText: { fontSize: 15, textAlign: 'center' },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  scopeGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  scopeButton: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: RADIUS.lg,
    borderWidth: 1,
  },
  scopeButtonText: { fontSize: 13, fontWeight: '600' },
  createButton: {
    alignSelf: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  createButtonText: { fontSize: 14, fontWeight: '600' },
  section: { gap: 10 },
  sectionHeader: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  row: { gap: 10 },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  rowHeaderRight: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  rowEditButton: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: RADIUS.sm,
    borderWidth: 1,
  },
  rowEditText: { fontSize: 13, fontWeight: '600' },
  title: { flex: 1, fontSize: 16, fontWeight: '600' },
  rowMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  cadence: { fontSize: 13 },
  progressWrap: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  progress: { fontSize: 14, fontWeight: '700' },
  noPeriod: { fontSize: 16, fontWeight: '700' },
  completedPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  completedText: { fontSize: 11, fontWeight: '700' },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 12,
  },
  chipText: { fontSize: 11, fontWeight: '600' },
});
